import React, { useEffect } from 'react';
import { 
  Hammer, Building, Wrench, Truck, Shield, Award, 
  CheckCircle, ArrowRight, Star, MessageSquare, Phone,
  Trash2, Palette, Users, ClipboardList, Pencil, Anvil
} from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import Logo from '../components/Logo';

const Services = () => {
  useEffect(() => {
    document.title = "Our Services - Ceballos Total Framing | Wood & Steel Framing Specialists";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional wood and steel framing services in Arizona. Custom timber selection, exposed beams, structural steel, demolition, siding, and project management. CR-7 Licensed.');
    }

    // Handle hash navigation
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  const woodFramingServices = [
    'Douglas Fir and Southern Yellow Pine premium lumber',
    'Precision connections using Simpson tie downs and hangers',
    'Advanced moisture management systems',
    'Seismic-resistant engineering techniques',
    'Custom beam fabrication and installation',
    'Exposed structural elements for architectural appeal'
  ];

  const steelFramingServices = [
    '16-20 gauge galvanized steel framing systems',
    'Custom welded connections and reinforcements',
    'Long-span capabilities for open concepts',
    'Corrosion-resistant finishing systems',
    'Precision fabrication and installation',
    'Structural steel beams and columns'
  ];

  const additionalServices = [
    {
      title: 'Clean Demolition',
      icon: Trash2,
      description: 'Precise removal of existing structures while preserving surrounding elements.',
      features: [
        'Selective structural removal',
        'Debris management and disposal',
        'Site protection protocols',
        'Environmental compliance'
      ]
    },
    {
      title: 'Premium Siding Installation',
      icon: Building,
      description: 'Professional installation of high-end siding systems for luxury residential projects.',
      features: [
        'Premium siding material selection',
        'Natural wood siding systems',
        'Professional installation techniques',
        'Architectural detailing'
      ]
    },
    {
      title: 'Architectural Integration',
      icon: Pencil,
      description: 'Seamless coordination with architects and designers for complex structural elements.',
      features: [
        'Design consultation services',
        'Structural feasibility analysis',
        'Custom fabrication solutions',
        'Engineering coordination'
      ]
    },
    {
      title: 'Elite Project Management',
      icon: ClipboardList,
      description: 'Comprehensive project oversight ensuring timeline adherence and quality control.',
      features: [
        'Daily progress monitoring',
        'Trade coordination',
        'Material logistics management',
        'Quality assurance protocols'
      ]
    }
  ];

  const pricingDetails = [
    {
      title: 'Transparent Pricing',
      description: 'Materials + labor with detailed breakdowns for every project component.',
      icon: CheckCircle
    },
    {
      title: 'Change Order Management',
      description: 'Clear documentation and approval process for any project modifications.',
      icon: CheckCircle
    },
    {
      title: 'Comprehensive Estimates',
      description: 'Detailed proposals including materials, timeline, methodology, and warranty.',
      icon: CheckCircle
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900 overflow-hidden">
      
      {/* Architectural Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-72">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="/FountainHills_KimberlyBucker1.jpg" 
            alt="Services Hero Background - Fountain Hills Kimberly Bucker Project"
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.5) contrast(1.1)' }}
          />
          <div className="absolute inset-0 bg-gray-900/40"></div>
        </div>

        <div className="relative z-20 text-center text-white px-6 max-w-8xl mx-auto">
          <AnimatedSection delay={200}>
            <div className="text-center">
              <div className="inline-block mb-8">

              </div>
              <h1 className="text-5xl md:text-7xl font-light mb-8 text-white leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Master <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Craftsmanship</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                Comprehensive structural framing services for Arizona's most distinguished projects
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Dual Mastery Section - Redesigned */}
      <div id="wood-steel-mastery" style={{ scrollMarginTop: '120px' }}></div>
      <section className="py-32 bg-gray-800 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-gradient-to-r from-gray-500 to-gray-600 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center mb-20">
              <div className="inline-block mb-8">
                
              </div>
              <h2 className="text-4xl md:text-6xl font-light mb-6 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Dual <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Mastery</span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto font-light leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                Where traditional craftsmanship meets modern engineering excellence
              </p>
            </div>
          </AnimatedSection>

          {/* Central Divider Design */}
          <div className="relative mb-20">
            <div className="flex items-center justify-center">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-amber-500/60"></div>
              <div className="mx-8 w-16 h-16 bg-gradient-to-r from-amber-500 to-gray-500 rounded-full flex items-center justify-center">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-gradient-to-r from-amber-500 to-gray-500 rounded-full"></div>
                </div>
              </div>
              <div className="flex-1 h-px bg-gradient-to-l from-transparent via-gray-500/30 to-gray-500/60"></div>
            </div>
          </div>
          
          {/* Alternating Layout */}
          <div className="space-y-32">
            
            {/* Wood Section - Left Focus */}
            <AnimatedSection delay={400}>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-7">
                  <div className="relative">
                    <div className="absolute -top-4 -left-4 w-24 h-24 bg-gradient-to-r from-amber-500/20 to-amber-600/20 rounded-full blur-xl"></div>
                    <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 p-10 border-l-4 border-amber-500 shadow-2xl" style={{ borderRadius: '16px' }}>
                      <div className="flex items-center mb-6">
                        <Hammer className="h-8 w-8 text-amber-500 mr-4" />
                        <h3 className="text-3xl font-medium text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Premium Wood Mastery</h3>
                      </div>
                      
                      <p className="text-gray-300 text-lg mb-8 leading-relaxed font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Premium materials and precision craftsmanship for superior structural integrity.
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex items-start">
                          <div className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                          <span className="text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>All premium structural lumber grades</span>
                        </div>
                        <div className="flex items-start">
                          <div className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                          <span className="text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Professional grade structural connections and fasteners</span>
                        </div>
                        <div className="flex items-start">
                          <div className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                          <span className="text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Engineered load-bearing calculations and design</span>
                        </div>
                        <div className="flex items-start">
                          <div className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                          <span className="text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Superior structural stability and longevity</span>
                        </div>
                        <div className="flex items-start">
                          <div className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                          <span className="text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Truss design and engineering coordination</span>
                        </div>
                        <div className="flex items-start">
                          <div className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                          <span className="text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Direct truss engineer collaboration</span>
                        </div>
                      </div>
                      

                    </div>
                  </div>
                </div>
                
                <div className="lg:col-span-5">
                  <div className="relative overflow-hidden rounded-2xl shadow-2xl h-full">
                    <img 
                      src="/ParadiseValley_McIntyreDevelopmentInterior2.jpg" 
                      alt="Premium Wood Framing Project - Paradise Valley McIntyre Development"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/40 to-transparent"></div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <h4 className="text-2xl font-medium text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Traditional Excellence</h4>
                      <p className="text-gray-200 font-light leading-relaxed mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Time-tested techniques enhanced with modern precision for structures that stand the test of time.
                      </p>
                      <a 
                        href="/portfolio"
                        className="inline-flex items-center text-amber-400 hover:text-amber-300 transition-colors duration-300 font-medium"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        View Wood Projects
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Steel Section - Right Focus */}
            <AnimatedSection delay={600}>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-5 order-2 lg:order-1">
                  <div className="relative overflow-hidden rounded-2xl shadow-2xl h-full">
                    <img 
                      src="/PuriResidenceSymmetryBuilders5.jpg" 
                      alt="Advanced Steel Structural Project - Puri Residence Symmetry Builders"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/40 to-transparent"></div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <h4 className="text-2xl font-medium text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Modern Innovation</h4>
                      <p className="text-gray-200 font-light leading-relaxed mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Advanced steel structural for maximum structural performance in today's ambitious architectural designs.
                      </p>
                      <a 
                        href="/portfolio"
                        className="inline-flex items-center text-gray-400 hover:text-gray-300 transition-colors duration-300 font-medium"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        View Steel Projects
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </div>
                
                <div className="lg:col-span-7 order-1 lg:order-2">
                  <div className="relative">
                    <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-r from-gray-500/20 to-gray-600/20 rounded-full blur-xl"></div>
                    <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 p-10 border-r-4 border-gray-500 shadow-2xl" style={{ borderRadius: '16px' }}>
                      <div className="flex items-center mb-6">
                        <Anvil className="h-8 w-8 text-gray-400 mr-4" />
                        <h3 className="text-3xl font-medium text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Advanced Steel Structural</h3>
                      </div>
                      
                      <p className="text-gray-300 text-lg mb-8 leading-relaxed font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Premium steel sourced from high-quality Arizona suppliers and expert installation for maximum structural performance in challenging designs.
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex items-start">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                          <span className="text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Complete range of structural steel framing systems</span>
                        </div>
                        <div className="flex items-start">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                          <span className="text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Custom welded connections and reinforcements</span>
                        </div>
                        <div className="flex items-start">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                          <span className="text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Structural steel beams and columns</span>
                        </div>
                        <div className="flex items-start">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                          <span className="text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Exposed steel design and finishing</span>
                        </div>
                        <div className="flex items-start">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                          <span className="text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Long-span capabilities for open concepts</span>
                        </div>
                        <div className="flex items-start">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                          <span className="text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Precise measurements and perfect alignment</span>
                        </div>
                      </div>
                      

                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>

          {/* Bottom Summary */}
          <AnimatedSection delay={800}>
            <div className="mt-32 text-center">
              <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 p-12 border border-amber-500/20 shadow-2xl" style={{ borderRadius: '16px' }}>
                <h4 className="text-3xl font-light text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  The <span className="bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Perfect</span> Combination
                </h4>
                <p className="text-xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Our mastery of both wood and steel allows us to deliver hybrid solutions that maximize the strengths of each material, 
                  creating structures that are both beautiful and built to perfection.
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Additional Services Section */}
      <section className="py-32 bg-gray-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center mb-20">
              <div className="inline-block mb-8">
                
              </div>
              <h2 className="text-4xl md:text-6xl font-light mb-12 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Comprehensive <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Services</span>
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {additionalServices.map((service, index) => (
              <AnimatedSection key={index} delay={400 + index * 200}>
                <div className="bg-gray-800 p-8 border border-amber-400/20 hover:border-amber-400/40 transition-all duration-500 h-full" style={{ borderRadius: '12px' }}>
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center mr-6" style={{ borderRadius: '8px' }}>
                      <service.icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-medium text-white" style={{ fontFamily: 'Playfair Display, serif' }}>{service.title}</h3>
                  </div>
                  
                  <p className="text-gray-300 leading-relaxed font-light mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {service.description}
                  </p>
                  
                  <ul className="space-y-3 text-gray-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-amber-400 mr-3 mt-1 flex-shrink-0" />
                        <span className="font-light">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing & Process Section */}
      <section className="py-32 bg-gray-800 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center mb-20">
              <div className="inline-block mb-8">
                
              </div>
              <h2 className="text-4xl md:text-6xl font-light mb-12 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Pricing & <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Process</span>
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            <AnimatedSection delay={400}>
              <div className="bg-gray-900 p-12 border border-amber-400/20" style={{ borderRadius: '12px' }}>
                <h3 className="text-3xl font-medium text-white mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>Our Pricing Philosophy</h3>
                
                <div className="space-y-8">
                  {pricingDetails.map((detail, idx) => (
                    <div key={idx} className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-amber-400 text-gray-900 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                        <detail.icon className="h-4 w-4" />
                      </div>
                      <div>
                        <h4 className="text-xl font-medium text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{detail.title}</h4>
                        <p className="text-gray-300 font-light leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>{detail.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={600}>
              <div className="space-y-8">
                <div className="bg-gray-900 p-8 border border-amber-400/20" style={{ borderRadius: '12px' }}>
                  <h4 className="text-2xl font-medium text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>What's Included</h4>
                  <div className="space-y-3 text-gray-300" style={{ fontFamily: 'Inter, sans-serif' }}>
                    <div className="flex items-center justify-between py-2">
                      <span className="font-light">Premium materials sourcing</span>
                      <CheckCircle className="h-5 w-5 text-amber-400" />
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="font-light">Expert installation labor</span>
                      <CheckCircle className="h-5 w-5 text-amber-400" />
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="font-light">Project management</span>
                      <CheckCircle className="h-5 w-5 text-amber-400" />
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="font-light">Quality assurance</span>
                      <CheckCircle className="h-5 w-5 text-amber-400" />
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="font-light">Warranty coverage</span>
                      <CheckCircle className="h-5 w-5 text-amber-400" />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-900 p-8 border border-amber-400/20" style={{ borderRadius: '12px' }}>
                  <h4 className="text-2xl font-medium text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Service Areas</h4>
                  <div className="grid grid-cols-2 gap-4 text-gray-300" style={{ fontFamily: 'Inter, sans-serif' }}>
                    <div className="font-light">Paradise Valley</div>
                    <div className="font-light">North Scottsdale</div>
                    <div className="font-light">Arcadia</div>
                    <div className="font-light">Phoenix</div>
                    <div className="font-light">Desert Mountain</div>
                    <div className="font-light">Custom Projects Statewide</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-32 bg-gray-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center">
              <h2 className="text-4xl md:text-6xl font-light mb-8 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Ready to Discuss Your <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Project</span>?
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12 font-light leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                Let's bring your architectural vision to life with uncompromising structural excellence.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <a 
                  href="/contact"
                  className="group relative overflow-hidden bg-gradient-to-r from-amber-500 to-amber-600 text-white px-12 py-4 transition-all duration-500 transform hover:scale-105 shadow-2xl hover:shadow-amber-500/25 text-lg font-medium tracking-wide"
                  style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-600 to-amber-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" style={{ borderRadius: '8px' }}></div>
                  <span className="relative z-10 flex items-center">
                    Get Your Quote
                    <ArrowRight className="ml-3 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </a>
                
                <a 
                  href="tel:602-448-1723"
                  className="group border border-amber-400/60 text-white px-12 py-4 hover:bg-amber-400/10 transition-all duration-500 backdrop-blur-md shadow-xl text-lg font-medium tracking-wide"
                  style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                >
                  <span className="flex items-center">
                    Call Now
                    <Phone className="ml-3 h-5 w-5 transform group-hover:scale-110 transition-transform duration-300" />
                  </span>
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Services;